/**
 * useAdmin — admin authentication + settings hooks.
 *
 * Two separate gating systems live in this codebase:
 *   1. `user_roles` table — used by AdminRoute (legacy)
 *   2. `admin_users` table — used by every admin service / page
 *
 * `useAdminAuth` queries `admin_users` because that's the source of
 * truth for admin services (audit log writes attribute to
 * admin_users.id; entitlement/credit logging uses it too). The
 * AdminRoute legacy gate still works in parallel until consolidation.
 *
 * `useAdminSettings` is the read/update hook for the admin_settings
 * key-value table — beta_mode, pause_automated_emails, etc.
 *
 * Used by:
 *   - components/admin/AdminLayout.tsx (sidebar, logout)
 *   - pages/admin/AdminSettings.tsx (settings toggles)
 *   - components/shared/AppSidebar.tsx (admin link visibility)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  checkIsAdmin,
  fetchAdminSettings,
  updateAdminSetting,
  type AdminUser,
  type AdminRole,
  type AdminSettings,
} from '@/services/admin';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

interface AdminAuthState {
  isAdmin: boolean;
  isSuperAdmin: boolean;
  role: AdminRole | null;
  adminUser: AdminUser | null;
  isLoading: boolean;
}

/**
 * Returns whether the current user has an active row in admin_users.
 * Re-queries when the auth user changes. Cached for 5 minutes since
 * admin role changes are rare and we don't need realtime here.
 *
 * Returns a flat shape (not React Query's `{ data, isLoading }`) so
 * callers can destructure { isAdmin, isSuperAdmin } directly.
 */
export const useAdminAuth = (): AdminAuthState => {
  const { user, loading: authLoading } = useAuth();

  const query = useQuery({
    queryKey: ['admin-auth', user?.id],
    queryFn: async () => {
      if (!user) {
        return { isAdmin: false, isSuperAdmin: false, role: null, adminUser: null };
      }
      const { isAdmin, role, adminUser } = await checkIsAdmin();
      return {
        isAdmin,
        isSuperAdmin: role === 'super_admin',
        role,
        adminUser,
      };
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  return {
    isAdmin: query.data?.isAdmin ?? false,
    isSuperAdmin: query.data?.isSuperAdmin ?? false,
    role: query.data?.role ?? null,
    adminUser: query.data?.adminUser ?? null,
    // DEVMODE-1 — while the session itself is still loading the query
    // is disabled, and a disabled query reports `isLoading: false`.
    // AdminRoute read that as "not an admin" on a direct load of any
    // /admin URL and bounced to /admin/login before the session
    // arrived. Auth loading counts as loading.
    isLoading: authLoading || (!!user && query.isLoading),
  };
};

/**
 * Quick boolean for callers that only care if the current user is an
 * admin — sidebar visibility, conditional menu items, etc.
 */
export const useIsAdmin = (): boolean => {
  const { isAdmin } = useAdminAuth();
  return isAdmin;
};

/**
 * Read + update admin_settings (beta_mode, pause_automated_emails,
 * session timeouts, email window). The mutation auto-invalidates
 * the cached settings so the toggle UI reflects the new value
 * immediately.
 */
export const useAdminSettings = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const settingsQuery = useQuery<AdminSettings>({
    queryKey: ['admin-settings'],
    queryFn: fetchAdminSettings,
    staleTime: 60_000,
  });

  const updateMutation = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: unknown }) => {
      await updateAdminSetting(key, value);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
      toast({ title: 'Setting updated' });
    },
    onError: (err) => {
      toast({
        title: 'Failed to update setting',
        description: err instanceof Error ? err.message : undefined,
        variant: 'destructive',
      });
    },
  });

  return {
    settings: settingsQuery.data,
    isLoading: settingsQuery.isLoading,
    /// Flat-named for ergonomic destructuring at call sites, e.g.
    /// `const { settings, updateSetting } = useAdminSettings()`.
    updateSetting: updateMutation.mutate,
    update: updateMutation.mutate, // legacy alias
    isUpdating: updateMutation.isPending,
  };
};

/**
 * List + invite + promote admin users.
 *
 * `invite(email, role)` adds a row to admin_invites — the recipient
 * gets a magic-link signup. If the recipient already has an
 * Orbit account (auth.users row exists), `promote(userId, role)`
 * upgrades them to admin directly without an email round-trip,
 * which is the path you actually want for promoting your own team.
 *
 * Both flows write to admin_audit_log so we have a paper trail of
 * who granted what to whom and when.
 */
export const useAdminUsers = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const auth = useAdminAuth();

  const adminsQuery = useQuery<AdminUser[]>({
    queryKey: ['admin-users-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as unknown as AdminUser[];
    },
    enabled: !!auth?.isAdmin,
  });

  const promoteMutation = useMutation({
    mutationFn: async ({
      email,
      role,
    }: { email: string; role: AdminRole }) => {
      // Find the auth.users row for this email — if they have an
      // Orbit account already, promote directly. If not, fall
      // through to invite-flow.
      const { data: profile } = await supabase
        .from('profiles')
        .select('id, full_name')
        .ilike('email', email.trim())
        .maybeSingle();

      if (profile?.id) {
        const { error } = await supabase
          .from('admin_users')
          .upsert(
            {
              user_id: profile.id,
              email: email.trim().toLowerCase(),
              name: profile.full_name ?? null,
              role,
              status: 'active',
            },
            { onConflict: 'user_id' },
          );
        if (error) throw error;

        // Audit log — log the promotion. logAdminAction is
        // imported lazily here to avoid a circular dep.
        const { logAdminAction } = await import('@/services/admin');
        await logAdminAction({
          action_type: 'admin_promoted',
          target_type: 'admin_user',
          target_id: profile.id,
          after_json: { email, role },
        });
        return { mode: 'promoted' as const };
      }

      // No existing account → fall back to email invite path.
      const token = crypto.randomUUID();
      const expires = new Date(Date.now() + 7 * 24 * 3600 * 1000).toISOString();
      const { error } = await supabase.from('admin_invites').insert({
        email: email.trim().toLowerCase(),
        invited_by: auth?.adminUser?.id ?? null,
        role,
        token,
        expires_at: expires,
      });
      if (error) throw error;
      return { mode: 'invited' as const };
    },
    onSuccess: (result, vars) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users-list'] });
      toast({
        title:
          result.mode === 'promoted'
            ? `${vars.email} is now an admin`
            : `Invited ${vars.email}`,
      });
    },
    onError: (err) => {
      toast({
        title: 'Could not add admin',
        description: err instanceof Error ? err.message : undefined,
        variant: 'destructive',
      });
    },
  });

  const suspendMutation = useMutation({
    mutationFn: async (adminUserId: string) => {
      const { error } = await supabase
        .from('admin_users')
        .update({ status: 'suspended' })
        .eq('id', adminUserId);
      if (error) throw error;
      const { logAdminAction } = await import('@/services/admin');
      await logAdminAction({
        action_type: 'admin_suspended',
        target_type: 'admin_user',
        target_id: adminUserId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users-list'] });
      toast({ title: 'Admin suspended' });
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: async (adminUserId: string) => {
      const { error } = await supabase
        .from('admin_users')
        .update({ status: 'active' })
        .eq('id', adminUserId);
      if (error) throw error;
      const { logAdminAction } = await import('@/services/admin');
      await logAdminAction({
        action_type: 'admin_reactivated',
        target_type: 'admin_user',
        target_id: adminUserId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users-list'] });
      toast({ title: 'Admin reactivated' });
    },
  });

  return {
    admins: adminsQuery.data ?? [],
    isLoading: adminsQuery.isLoading,
    promote: promoteMutation.mutate,
    isPromoting: promoteMutation.isPending,
    suspend: suspendMutation.mutate,
    reactivate: reactivateMutation.mutate,
  };
};
